import {
  Body1,
  Body2,
  Button,
  Card,
  Checkbox,
  Display1,
  Display2,
  Grid,
  GridItem,
  Label1,
  Label2,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Tabs,
} from '@sainsburys-tech/fable'
import { SparkFilled } from '@sainsburys-tech/icons'
import moment from 'moment'
import React, { useState } from 'react'

import AppTooltip from '../../../common/components/Tooltip/AppTooltip'
import { IDailyIngredientQuantity } from '../../../common/types/IDailyIngredientQuantity'
import { IIngredient } from '../../../common/types/IIngredient'
import { roundUpToTwoDecimalPlace } from '../../../common/utils/roundUpToTwoDecimalPlace'
import { ISubCategory } from '../../types/ISubCategory'

interface IProps {
  handleIngredientChecked: (ingredientId: number) => void
  handleDeselectAll: () => void
  handleSelectAll: (subCategoryId: number) => void
  ingredients: IIngredient[]
  initialTab: number
  subCategories: ISubCategory[]
}

const IngredientTable = (props: IProps) => {
  // Removed unused tabs variable

  const [currentTab, setCurrentTab] = useState(props.initialTab)
  const totalCasesTooltip = (
    <AppTooltip
      message={
        "This is the total number of cases you'll need for each SKU for the dates shown."
      }
    />
  )
  const requiredSkusTooltip = (
    <AppTooltip
      message={
        'Selected SKUs can be printed to support with stock checks before ordering.'
      }
    />
  )
  const date = (days: number) => moment().add(days, 'days').format('ddd DD/MM')

  const ingredientTitle = (ingredient: IIngredient) => (
    <>
      (<b>{ingredient.ingredientId}</b>) {ingredient.ingredientName}
      {ingredient.isCredibleProduct && (
        <SparkFilled className='c-star__icon' />
      )}
    </>
  )

  const getDailyQuantities = (
    dailyIngredient: IDailyIngredientQuantity[],
    totalCaseUnits: number,
  ) => {
    const dailyQuantities = new Array<number | string>(7).fill('TBC')

    dailyIngredient.forEach((dailyQuantity, i) => {
      dailyQuantities[i] = roundUpToTwoDecimalPlace(
        dailyQuantity.quantity / totalCaseUnits,
      )
    })

    return dailyQuantities
  }

  const total = (
    dailyIngredients: IDailyIngredientQuantity[],
    totalCaseUnits: number,
  ) => {
    let res = 0
    dailyIngredients.map(
      (dailyQuantity) =>
        (res += parseFloat(
          roundUpToTwoDecimalPlace(dailyQuantity.quantity / totalCaseUnits),
        )),
    )
    return roundUpToTwoDecimalPlace(res)
  }

  const dayAndQuantity = (dailyQuantity: any[], num: number) => (
    <>
      <GridItem colSpan={6}>
        <Display1>{date(num)}</Display1>
      </GridItem>
      <GridItem colSpan={6}>
        <Display1>{dailyQuantity[num]}</Display1>
      </GridItem>
    </>
  )

  const ingredientCard = () =>
    props.ingredients.map((ingredient: IIngredient, key: number) => {
      const dailyQuantity = getDailyQuantities(
        ingredient.dailyIngredients,
        ingredient.totalCaseUnits,
      )
      return (
        <div
          className='c-bakery-card-container c-bakery-card-row c-common-hide-in-print'
          key={key}
        >
          <div>
            <Display2>{ingredientTitle(ingredient)}</Display2>
          </div>
          <GridItem colSpan={6}>
            <Display1>{moment().format('ddd DD/MM')}</Display1>
          </GridItem>
          <GridItem colSpan={6}>
            <Display1>{dailyQuantity[0]}</Display1>
          </GridItem>
          {dayAndQuantity(dailyQuantity, 1)}
          {dayAndQuantity(dailyQuantity, 2)}
          {dayAndQuantity(dailyQuantity, 3)}
          {dayAndQuantity(dailyQuantity, 4)}
          {dayAndQuantity(dailyQuantity, 5)}
          {dayAndQuantity(dailyQuantity, 6)}
          <GridItem colSpan={6}>
            <Display1>Total cases</Display1>
          </GridItem>
          <GridItem colSpan={6}>
            <Display1>
              {total(ingredient.dailyIngredients, ingredient.totalCaseUnits)}
            </Display1>
          </GridItem>
        </div>
      )
    })

  const getIngredientsTable = (ingredients: IIngredient[]) =>
    ingredients.map((ingredient, key) => {
      const dailyQuantity = getDailyQuantities(
        ingredient.dailyIngredients,
        ingredient.totalCaseUnits,
      )
      return (
        <TableRow key={key}>
          <TableCell
            className='c-bakery-ingredient-sku'
            data-testid='ingredientTitle'
          >
            <Body1>{ingredientTitle(ingredient)}</Body1>
          </TableCell>
          <TableCell
            className='c-bakery-ingredient-quantity'
            data-testid='qty1'
          >
            <Body1>{dailyQuantity[0]}</Body1>
          </TableCell>
          <TableCell
            className='c-bakery-ingredient-quantity'
            data-testid='qty2'
          >
            <Body1>{dailyQuantity[1]}</Body1>
          </TableCell>
          <TableCell
            className='c-bakery-ingredient-quantity'
            data-testid='qty3'
          >
            <Body1>{dailyQuantity[2]}</Body1>
          </TableCell>
          <TableCell
            className='c-bakery-ingredient-quantity'
            data-testid='qty4'
          >
            <Body1>{dailyQuantity[3]}</Body1>
          </TableCell>
          <TableCell
            className='c-bakery-ingredient-quantity'
            data-testid='qty5'
          >
            <Body1>{dailyQuantity[4]}</Body1>
          </TableCell>
          <TableCell
            className='c-bakery-ingredient-quantity'
            data-testid='qty6'
          >
            <Body1>{dailyQuantity[5]}</Body1>
          </TableCell>
          <TableCell
            className='c-bakery-ingredient-quantity'
            data-testid='qty7'
          >
            <Body1>{dailyQuantity[6]}</Body1>
          </TableCell>
          <TableCell
            className='c-bakery-ingredient-total-cases'
            data-testid='ingredientTotal'
          >
            <span>
              <Label1>
                {total(ingredient.dailyIngredients, ingredient.totalCaseUnits)}
              </Label1>
            </span>
          </TableCell>
          <TableCell className='c-bakery-ingredient-required-skus c-common-hide-in-print'>
            <div>
              <Checkbox
                isStandalone
                label={ingredient.ingredientId.toString()}
                id={ingredient.ingredientId.toString()}
                name={ingredient.ingredientId.toString()}
                isChecked={ingredient.checked === true}
                onChange={() =>
                  props.handleIngredientChecked(ingredient.ingredientId)
                }
                testid='ingredient-checkbox'
              />
            </div>
          </TableCell>
        </TableRow>
      )
    })

  const ingredientTable = (
    ingredients: IIngredient[],
    subCategoryId?: number,
  ) => (
    <>
      <div className='c-bakery-ingredient-table-wrapper'>
        <Table testid='ingredient-table' className='c-bakery-ingredient-table'>
          <TableHeader>
            <TableRow>
              <TableCell className='c-bakery-ingredient-sku'>
                <Label2>SKU/units per case</Label2>
              </TableCell>
              <TableCell
                className='c-bakery-ingredient-quantity'
                data-testid='ingDate1'
              >
                <Label2>{moment().format('ddd DD/MM')}</Label2>
              </TableCell>
              <TableCell
                className='c-bakery-ingredient-quantity'
                data-testid='ingDate2'
              >
                <Label2>{date(1)}</Label2>
              </TableCell>
              <TableCell
                className='c-bakery-ingredient-quantity'
                data-testid='ingDate3'
              >
                <Label2>{date(2)}</Label2>
              </TableCell>
              <TableCell
                className='c-bakery-ingredient-quantity'
                data-testid='ingDate4'
              >
                <Label2>{date(3)}</Label2>
              </TableCell>
              <TableCell
                className='c-bakery-ingredient-quantity'
                data-testid='ingDate5'
              >
                <Label2>{date(4)}</Label2>
              </TableCell>
              <TableCell
                className='c-bakery-ingredient-quantity'
                data-testid='ingDate6'
              >
                <Label2>{date(5)}</Label2>
              </TableCell>
              <TableCell
                className='c-bakery-ingredient-quantity'
                data-testid='ingDate7'
              >
                <Label2>{date(6)}</Label2>
              </TableCell>
              <TableCell
                className='c-bakery-ingredient-total-cases'
                data-testid='totalCases'
              >
                <Label2>Total cases{totalCasesTooltip}</Label2>
              </TableCell>
              <TableCell
                className='c-bakery-ingredient-required-skus c-common-hide-in-print'
                data-testid='requiredSku'
              >
                <Label2>Required SKUs{requiredSkusTooltip}</Label2>
              </TableCell>
            </TableRow>
          </TableHeader>
          <TableBody>{getIngredientsTable(ingredients)}</TableBody>
        </Table>
      </div>
      <GridItem colSpan={12} className='c-common-hide-in-print'>
        <Button
          testid='select-all-button'
          className='c-bakery-ingredient-select-all'
          onClick={() => props.handleSelectAll(subCategoryId ?? currentTab)}
        >
          Select all
        </Button>
      </GridItem>
      <div className='c-common-hide-in-print'>
        <Body2 style={{ paddingTop: '16px', paddingBottom: '8px' }}>
          Print checked items
        </Body2>
      </div>
      <div className='c-common-hide-in-print'>
        <Button
          onClick={() => window.print()}
          testid='print-button'
          variant='secondary'
        >
          Print
        </Button>
      </div>
    </>
  )

  return (
    <>
      <Card orientation='vertical' className='c-common-hide-in-print'>
        <Grid>
          <GridItem colSpan={12} className='c-common-hide-in-print'>
            <Tabs
              activeTab={currentTab.toString()}
              onChange={(value) => setCurrentTab(Number(value))}
              testid='ingredient-tabs'
            >
              {props.subCategories.map((subcat) => (
                <Tab
                  key={subcat.subCategoryId}
                  label={subcat.subCategoryName}
                  value={subcat.subCategoryId.toString()}
                  testid={`ingredient-tab-${subcat.subCategoryName}`}
                >
                  {ingredientTable(
                    props.ingredients.filter(
                      (x) => x.subCategoryId === subcat.subCategoryId,
                    ),
                    subcat.subCategoryId,
                  )}
                  {ingredientCard()}
                </Tab>
              ))}
            </Tabs>
          </GridItem>
        </Grid>
      </Card>
      <GridItem className='c-common-only-visible-in-print' colSpan={12}>
        {ingredientTable(props.ingredients.filter((x) => x.checked === true))}
      </GridItem>
    </>
  )
}

export default IngredientTable
